import React from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";

import dscsaSrc from "./dscsaCompliant.svg";
import fedexSrc from "./Fedex-logo.png";
import stripeSrc from "./powered_by_stripe.png";

const INK_SOFT = "#475569";
const LINE = "#E2E8F0";

const legalLinkSx = {
  color: INK_SOFT,
  fontSize: "12.5px",
  "&:hover": { color: "#1D4ED8" },
};

const brandMarkSx = {
  display: "inline-flex",
  alignItems: "center",
  lineHeight: 0,
  "& img": {
    display: "block",
    width: "auto",
    height: 20,
    objectFit: "contain",
  },
};

const partnerLogoSx = {
  ...brandMarkSx,
  "& img": {
    ...brandMarkSx["& img"],
    height: 26,
  },
};

const SOCIALS = [
  {
    label: "X (Twitter)",
    href: "https://twitter.com/PrimePostRx",
    path: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.451-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z",
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/",
    path: "M22 12.06C22 6.505 17.523 2 12 2S2 6.505 2 12.06c0 5.022 3.657 9.184 8.438 9.94v-7.03H7.898v-2.91h2.54V9.845c0-2.522 1.492-3.915 3.777-3.915 1.094 0 2.238.196 2.238.196v2.476h-1.26c-1.243 0-1.63.775-1.63 1.57v1.888h2.773l-.443 2.91h-2.33V22c4.78-.756 8.437-4.918 8.437-9.94z",
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/primepostrx/",
    path: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98C.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.667.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z",
  },
];

export const BrandFooter: React.FunctionComponent = () => {
  return (
    <Box
      component="footer"
      sx={{
        borderTop: `1px solid ${LINE}`,
        bgcolor: "#F8FAFC",
        paddingX: { xs: 3, md: 4 },
        paddingY: 2,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        fontSize: "12.5px",
        color: INK_SOFT,
        fontFamily: '"Source Sans 3", sans-serif',
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2.5 }}>
        <span>© {new Date().getFullYear()} PrimePostRx. All rights reserved.</span>
        <Link href="https://www.primepostrx.com/privacy" target="_blank" underline="none" sx={legalLinkSx}>
          Privacy Policy
        </Link>
        <Link href="https://www.primepostrx.com/user-agreement" target="_blank" underline="none" sx={legalLinkSx}>
          User Agreement
        </Link>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
          {SOCIALS.map((social) => (
            <Link
              key={social.label}
              href={social.href}
              target="_blank"
              aria-label={social.label}
              sx={{
                color: INK_SOFT,
                display: "inline-flex",
                alignItems: "center",
                lineHeight: 0,
                "&:hover": { color: "#2563EB" },
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d={social.path} />
              </svg>
            </Link>
          ))}
        </Box>
        <Box sx={{ width: "1px", height: 16, bgcolor: LINE, flexShrink: 0 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Link href="https://www.primepostrx.com/dscsa-compliant" target="_blank" underline="none" sx={brandMarkSx}>
            <img src={dscsaSrc} alt="DSCSA compliant" />
          </Link>
          <Link href="https://www.fedex.com/" target="_blank" underline="none" sx={partnerLogoSx}>
            <img src={fedexSrc} alt="Ships via FedEx" />
          </Link>
          <Link href="https://stripe.com/" target="_blank" underline="none" sx={partnerLogoSx}>
            <img src={stripeSrc} alt="Powered by Stripe" />
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default BrandFooter;
